import { useState, useEffect } from 'react';
import { apiFetch } from '../api';

function KpiSummary({ refreshKey, extraRefreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAll(); }, [refreshKey, extraRefreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchAll() {
    try {
      const [acctRes, txRes, portfolioRes, goalsRes, pkgRes] = await Promise.all([
        apiFetch('/api/simplefin/accounts'),
        apiFetch('/api/simplefin/transactions'),
        apiFetch('/api/portfolio').catch(() => ({ ok: false })),
        apiFetch('/api/goals').catch(() => ({ ok: false })),
        apiFetch('/api/packages').catch(() => ({ ok: false })),
      ]);

      const accounts = acctRes.ok ? await acctRes.json() : [];
      const transactions = txRes.ok ? await txRes.json() : [];
      const portfolio = portfolioRes.ok ? await portfolioRes.json() : [];
      const goalsData = goalsRes.ok ? await goalsRes.json() : null;
      const packages = pkgRes.ok ? await pkgRes.json() : [];

      // Net worth
      let netWorth = 0;
      for (const a of accounts) netWorth += parseFloat(a.balance) || 0;

      // Portfolio day change
      let portfolioValue = 0, portfolioDayChange = 0;
      for (const h of portfolio) {
        portfolioValue += h.marketValue || 0;
        portfolioDayChange += h.dayGain || 0;
      }

      // This month spending & income
      const now = new Date();
      const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      let monthSpending = 0, monthIncome = 0, uncategorized = 0, unreviewed = 0;
      for (const tx of transactions) {
        const date = tx.posted || tx.transactedAt;
        if (!date || !date.startsWith(thisMonth)) continue;
        const amt = parseFloat(tx.amount) || 0;
        if (amt < 0 && tx.category !== 'Transfer') monthSpending += Math.abs(amt);
        if (amt > 0 && tx.category !== 'Transfer') monthIncome += amt;
        if (!tx.category && amt < 0) uncategorized++;
      }
      for (const tx of transactions) {
        if (!tx.isReviewed) unreviewed++;
      }

      // Goals completed today
      let goalsCompletedToday = 0, goalsTotalToday = 0, goalStreak = 0;
      if (goalsData && goalsData.goals) {
        const todayStr = now.toISOString().slice(0, 10);
        const dailyGoals = goalsData.goals.filter(g => g.frequency === 'daily');
        goalsTotalToday = dailyGoals.length;
        if (goalsData.completions) {
          goalsCompletedToday = goalsData.completions.filter(c => c.date === todayStr).length;

          // Calculate streak (consecutive days with all goals completed)
          for (let d = 1; d <= 90; d++) {
            const checkDate = new Date(now);
            checkDate.setDate(checkDate.getDate() - d);
            const dateStr = checkDate.toISOString().slice(0, 10);
            const completions = goalsData.completions.filter(c => c.date === dateStr).length;
            if (completions >= dailyGoals.length && dailyGoals.length > 0) {
              goalStreak++;
            } else {
              break;
            }
          }
          // Include today if all done
          if (goalsCompletedToday >= goalsTotalToday && goalsTotalToday > 0) goalStreak++;
        }
      }

      // Packages in transit
      const inTransit = packages.filter(p => p.status === 'in_transit' || p.status === 'out_for_delivery').length;

      setData({
        netWorth, portfolioValue, portfolioDayChange,
        monthSpending, monthIncome, uncategorized, unreviewed,
        goalsCompletedToday, goalsTotalToday, goalStreak,
        inTransit,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  if (loading || !data) {
    return <section className="card"><h2>Summary</h2><p className="loading">Loading...</p></section>;
  }

  function fmt(val) {
    if (Math.abs(val) >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
    if (Math.abs(val) >= 1000) return `$${(val / 1000).toFixed(1)}K`;
    return `$${val.toFixed(0)}`;
  }

  const kpis = [
    {
      label: 'Net Worth',
      value: fmt(data.netWorth),
      color: '#e4e4e7',
    },
    {
      label: 'Portfolio',
      value: fmt(data.portfolioValue),
      sub: `${data.portfolioDayChange >= 0 ? '+' : ''}${fmt(data.portfolioDayChange)} today`,
      color: data.portfolioDayChange >= 0 ? '#22c55e' : '#ef4444',
      subColor: data.portfolioDayChange >= 0 ? '#22c55e' : '#ef4444',
    },
    {
      label: 'Spending (MTD)',
      value: fmt(data.monthSpending),
      sub: `${fmt(data.monthIncome)} income`,
      color: '#ef4444',
      subColor: '#22c55e',
    },
    {
      label: 'Goals Today',
      value: `${data.goalsCompletedToday}/${data.goalsTotalToday}`,
      sub: data.goalStreak > 0 ? `${data.goalStreak} day streak` : null,
      color: data.goalsCompletedToday >= data.goalsTotalToday ? '#22c55e' : '#fbbf24',
      subColor: '#f97316',
    },
    {
      label: 'Unreviewed',
      value: data.unreviewed,
      sub: data.uncategorized > 0 ? `${data.uncategorized} uncategorized` : null,
      color: data.unreviewed > 20 ? '#fbbf24' : '#71717a',
      subColor: '#fbbf24',
    },
    {
      label: 'Packages',
      value: data.inTransit,
      sub: data.inTransit > 0 ? 'in transit' : 'none',
      color: data.inTransit > 0 ? '#38bdf8' : '#71717a',
    },
  ];

  return (
    <section className="card kpi-section">
      <h2>At a Glance</h2>
      <div className="kpi-grid">
        {kpis.map((kpi) => (
          <div key={kpi.label} className="kpi-card">
            <span className="kpi-value" style={{ color: kpi.color }}>{kpi.value}</span>
            <span className="kpi-label">{kpi.label}</span>
            {kpi.sub && <span className="kpi-sub" style={{ color: kpi.subColor || '#71717a' }}>{kpi.sub}</span>}
          </div>
        ))}
      </div>
    </section>
  );
}

export default KpiSummary;
